import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  BuildingCardContainer,
  BuildingHeader,
  BuildingIcon,
  BuildingStatusGroup,
  BuildingStatus,
  BuildingInfo,
  BuildingTitle,
  BuildingName,
  BuildingId,
  BuildingMeta,
  MetaItem,
  BuildingPercentage,
  BuildingActions,
} from '../BuildingDetailsTab.styles'
import { BuildingDetails } from './BuildingDetails'
import { Icon } from '../../../../../../atoms/Icon'
import { ExpandArrow } from '../../../../../../atoms/ExpandArrow'
import {
  type BuildingData,
  getBuildingIconType,
  getBuildingIcon,
  formatArea,
  BUILDING_VALUES,
} from '../utils/buildingData.utils'

interface BuildingCardProps {
  building: BuildingData
  isExpanded: boolean
  onToggleExpanded: () => void
}

export const BuildingCard: React.FC<BuildingCardProps> = ({
  building,
  isExpanded,
  onToggleExpanded,
}) => {
  const { t } = useTranslation('land-analysis')

  // Map marker values to translated strings
  const valueMap: Record<string, string> = {
    [BUILDING_VALUES.NOT_PROTECTED]: t('buildingDetails.values.notProtected'),
    [BUILDING_VALUES.HERITAGE_PROTECTED]: t(
      'buildingDetails.values.heritageProtected'
    ),
    [BUILDING_VALUES.COMPLIANT]: t('buildingDetails.values.compliant'),
    [BUILDING_VALUES.NOT_CLASSIFIED]: t('buildingDetails.values.notClassified'),
    [BUILDING_VALUES.NOT_SPECIFIED]: t('buildingDetails.values.notSpecified'),
    [BUILDING_VALUES.NA]: t('buildingDetails.values.na'),
    [BUILDING_VALUES.SOLAR_EXCELLENT]: t(
      'buildingDetails.values.solarExcellent'
    ),
    [BUILDING_VALUES.SOLAR_GOOD]: t('buildingDetails.values.solarGood'),
    [BUILDING_VALUES.SOLAR_MEDIUM]: t('buildingDetails.values.solarMedium'),
    [BUILDING_VALUES.SOLAR_LOW]: t('buildingDetails.values.solarLow'),
    [BUILDING_VALUES.PROJECTED]: t('buildingDetails.values.projected'),
    [BUILDING_VALUES.BUILDING_NAME]: t('buildingDetails.values.building', {
      index: building.nameIndex,
    }),
  }

  const tv = (value: string | undefined) => {
    if (!value) return ''
    return valueMap[value] ?? value
  }

  const buildingName = tv(building.name)

  const type = building?.popetyClassification?.toLocaleLowerCase() || ''
  const buildingTypeClass = `type-${type}`

  return (
    <BuildingCardContainer
      className={`${buildingTypeClass} ${!isExpanded ? 'collapsed' : ''}`}
    >
      <BuildingHeader onClick={onToggleExpanded}>
        <BuildingIcon
          className={getBuildingIconType(type)}
          title={tv(building.popetyClassification || '')}
        >
          <Icon name={getBuildingIcon(type) as any} size="medium" />
          <BuildingStatusGroup>
            {building.isProtected && (
              <BuildingStatus className="protected">
                <Icon name="warning" size="small" />
              </BuildingStatus>
            )}
            {building.isUnderConstruction && (
              <BuildingStatus className="under-construction">
                <Icon name="building-permit" size="small" />
              </BuildingStatus>
            )}
            {building.isUnderground && (
              <BuildingStatus className="underground">
                <Icon name="arrow-down" size="small" />
              </BuildingStatus>
            )}
          </BuildingStatusGroup>
        </BuildingIcon>

        <BuildingInfo>
          <BuildingTitle>
            <BuildingName>{buildingName}</BuildingName>
          </BuildingTitle>
          <BuildingId>
            {building.egid && `EGID ${building.egid}`}
            {building.egid && building.address && ' \u2022 '}
            {building.address}
          </BuildingId>
          <BuildingMeta>
            <MetaItem>
              <Icon name="square" size="small" />
              {tv(formatArea(building.area))}
            </MetaItem>
            <MetaItem>
              <Icon name="layers" size="small" />
              {building.floors} {t('header.floors')}
            </MetaItem>
            {building.constructionYear && (
              <MetaItem>
                <Icon name="calendar" size="small" />
                {building.constructionYear}
              </MetaItem>
            )}
            <BuildingPercentage
              className={building.percentage === 100 ? 'full' : 'partial'}
              title={t('buildingDetails.card.percentageTooltip')}
            >
              {building.percentage}%
            </BuildingPercentage>
          </BuildingMeta>
        </BuildingInfo>

        <BuildingActions>
          <ExpandArrow $expanded={isExpanded} direction="down" />
        </BuildingActions>
      </BuildingHeader>

      {isExpanded && <BuildingDetails building={building} />}
    </BuildingCardContainer>
  )
}
